/**
 * Google Login Button Component
 *
 * This component provides a Google OAuth2 login button.
 * It uses the @react-oauth/google library to handle authentication.
 *
 * Usage:
 * <GoogleLoginButton onSuccess={handleLoginSuccess} />
 *
 * Environment Variables Required:
 * REACT_APP_GOOGLE_CLIENT_ID - Your Google OAuth Client ID
 * REACT_APP_HOST_API - Your backend API URL (e.g., http://localhost:8000)
 */

import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Alert } from '@mui/material';
import axios from 'axios';

const GoogleLoginButton = ({ onSuccess, onError, fullWidth = false, variant = 'contained' }) => {
  const [error, setError] = useState(null);

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    setError(null);

    try {
      const idToken = credentialResponse.credential;

      if (!idToken) {
        throw new Error('No credential received from Google');
      }

      // Send the ID token to your backend
      const response = await axios.post(
        `${process.env.REACT_APP_HOST_API}/api/v1/auth/google/callback/`,
        {
          token: idToken,
        }
      );

      // Save tokens to localStorage (use same names as JWT login)
      localStorage.setItem('access', response.data.access);
      localStorage.setItem('refresh', response.data.refresh);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Call success callback
      if (onSuccess) {
        onSuccess(response.data);
      }

    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Authentication failed';
      setError(errorMessage);
      console.error('Google login error:', err);

      if (onError) {
        onError(err);
      }
    }
  };

  return (
    <div>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <GoogleLogin
        onSuccess={handleGoogleLoginSuccess}
        onError={() => {
          setError('Google login failed');
          console.error('Google Login Failed');
        }}
        theme="outline"
        size="large"
        text="signin_with"
        width={fullWidth ? "100%" : undefined}
      />
    </div>
  );
};

export default GoogleLoginButton;
